// src/pages/FinancePage/components/funnel-detail/FunnelDetailPage.tsx

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getFunnelFinanceDetail } from '../../services/financeService';
import { FunnelDetailData, Period, UnitConceptRow } from './types';
import { PeriodSelector } from './PeriodSelector';
import { createDefaultPeriod } from './periods';
import { FunnelStages } from './FunnelStages';
import { UnitTable } from './UnitTable';
import { FunnelTable } from './FunnelTable';
import { ChatLayoutWrapper, buildFunnelContext, ChatContext } from '../ai-chat';
import { FunnelMarketingTab } from './FunnelMarketingTab';

type FunnelDetailTab = 'finance' | 'marketing';

export function FunnelDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [period, setPeriod] = useState<Period>(() => createDefaultPeriod());
  const [expectedProfitPct, setExpectedProfitPct] = useState(25);
  const [baseData, setBaseData] = useState<FunnelDetailData | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState<FunnelDetailTab>('finance');

  const [unitRows, setUnitRows] = useState<UnitConceptRow[]>([]);
  const focusView = searchParams.get('view');

  useEffect(() => {
    const tab = searchParams.get('tab');
    const view = searchParams.get('view');
    if (view === 'marketing') {
      setActiveDetailTab('marketing');
    } else if (tab === 'marketing' || tab === 'finance') {
      setActiveDetailTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;
    setDetailError(null);
    getFunnelFinanceDetail(id, period)
      .then((detail) => {
        if (!mounted) return;
        setBaseData(detail);
        setUnitRows(detail.unitRows);
      })
      .catch(() => {
        if (!mounted) return;
        setBaseData(null);
        setUnitRows([]);
        setDetailError('No pudimos cargar este embudo desde Finance.');
      });
    return () => {
      mounted = false;
    };
  }, [id, period]);

  useEffect(() => {
    if (!baseData) return;
    setUnitRows(prev => baseData.unitRows.map(newRow => {
      const existing = prev.find(r => r.id === newRow.id);
      return existing ? { ...newRow, targetValue: existing.targetValue } : newRow;
    }));
  }, [baseData]);

  useEffect(() => {
    if (!baseData || !focusView) return;
    const targetId =
      focusView === 'marketing'
        ? 'finance-funnel-marketing-target'
        : focusView === 'logistics'
          ? 'finance-funnel-logistics-target'
          : 'finance-funnel-calculator-target';
    window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  }, [baseData, focusView, activeDetailTab]);

  const handleUnitTargetChange = (rowId: UnitConceptRow['id'], newValue: number) => {
    setUnitRows(prev => prev.map(r => r.id === rowId ? { ...r, targetValue: newValue } : r));
  };

  const chatContext: ChatContext = useMemo(() => ({
    surface: 'funnel',
    funnelId: id,
    period: {
      id: period.id,
      label: period.label,
      from: period.from.toISOString().slice(0, 10),
      to: period.to.toISOString().slice(0, 10),
      days: period.days,
    },
    funnel: baseData
      ? buildFunnelContext({
          funnelId: id,
          funnelName: baseData.funnelName,
          productName: baseData.productName,
          period: baseData.period,
          stages: baseData.stages,
          unitRows,
          expectedProfitPct,
          realDeliveries: baseData.stages.delivered,
        })
      : undefined,
  }), [baseData, expectedProfitPct, id, period, unitRows]);

  if (!baseData) {
    return (
      <div className="min-h-screen bg-[#F7F7F8]">
        <div className="max-w-[1100px] mx-auto px-6 py-6 text-sm text-gray-500">
          {detailError ? (
            <div className="rounded-lg border border-red-100 bg-red-50 px-5 py-4 font-medium text-red-700">
              {detailError}
            </div>
          ) : (
            "Cargando embudo..."
          )}
        </div>
      </div>
    );
  }

  return (
    <ChatLayoutWrapper context={chatContext}>
      <div className="min-h-screen bg-[#F7F7F8]">
        <div className="max-w-[1100px] mx-auto px-6 py-6">

          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg px-2 text-sm font-semibold text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
              type="button"
            >
              <ArrowLeft className="w-4 h-4" />
              Embudos
            </button>
          </div>

          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {baseData.funnelName}
                <span className="font-medium text-gray-500"> - {baseData.productName}</span>
              </h1>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="inline-flex w-fit rounded-full bg-gray-200 p-1">
                <button
                  type="button"
                  onClick={() => setActiveDetailTab('finance')}
                  className={
                    activeDetailTab === 'finance'
                      ? 'inline-flex h-9 items-center justify-center rounded-full bg-white px-5 text-sm font-semibold text-gray-900 shadow-sm'
                      : 'inline-flex h-9 items-center justify-center rounded-full px-5 text-sm font-semibold text-gray-500 transition hover:text-gray-800'
                  }
                >
                  Finanzas
                </button>
                <button
                  type="button"
                  onClick={() => setActiveDetailTab('marketing')}
                  className={
                    activeDetailTab === 'marketing'
                      ? 'inline-flex h-9 items-center justify-center rounded-full bg-white px-5 text-sm font-semibold text-gray-900 shadow-sm'
                      : 'inline-flex h-9 items-center justify-center rounded-full px-5 text-sm font-semibold text-gray-500 transition hover:text-gray-800'
                  }
                >
                  Marketing
                </button>
              </div>
              <PeriodSelector value={period} onChange={setPeriod} />
            </div>
          </div>

          {!baseData.dropiConnected && (
            <div className="mb-6 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="font-semibold">Logística manual activa.</span> Puedes trabajar este embudo sin Dropi. Cuando conectes Dropi, Finance completará costos y estados con datos reales.
            </div>
          )}

          {activeDetailTab === 'finance' ? (
            <>
              <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
                <FunnelStages stages={baseData.stages} />
              </div>

              <div className="space-y-6">
                <div id="finance-funnel-calculator-target" className="scroll-mt-6">
                  {focusView === 'price' || focusView === 'cpa' ? (
                    <FunnelFocusCard
                      title={focusView === 'price' ? 'Precio ideal' : 'CPA ideal'}
                      description={
                        focusView === 'price'
                          ? 'Calcula tu precio real y proyecta el precio necesario para sostener la utilidad esperada.'
                          : 'Compara tu CPA real contra el CPA máximo rentable y define qué tanto puedes pagar por compra.'
                      }
                    />
                  ) : null}
                  <UnitTable
                    rows={unitRows}
                    onTargetChange={handleUnitTargetChange}
                    expectedProfitPct={expectedProfitPct}
                    onExpectedProfitChange={setExpectedProfitPct}
                  />
                </div>
                <FunnelTable
                  unitRows={unitRows}
                  expectedProfitPct={expectedProfitPct}
                  realDeliveries={baseData.stages.delivered}
                  initialProjectedDeliveries={baseData.stages.delivered}
                />
                <div id="finance-funnel-logistics-target" className="scroll-mt-6 rounded-lg border border-gray-200 bg-white p-5">
                  <h2 className="text-base font-bold text-gray-900">Logística del embudo</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Próximamente reuniremos aquí estados, costos y riesgos logísticos específicos de este embudo.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div id="finance-funnel-marketing-target" className="scroll-mt-6">
              <FunnelMarketingTab data={{ ...baseData, unitRows }} period={period} />
            </div>
          )}
        </div>
      </div>
    </ChatLayoutWrapper>
  );
}

function FunnelFocusCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-5 py-4">
      <p className="text-sm font-bold text-[#2b5ce6]">{title}</p>
      <p className="mt-1 text-sm leading-5 text-gray-600">{description}</p>
    </div>
  );
}
